/**
 * /admin/campanhas — Central de AÇÕES de venda.
 *
 * Cada ação (Plantão FEO, Flash FEO) tem estado próprio e um botão único de
 * ativar/desativar que executa automaticamente o checklist técnico
 * (slides da home, /pos, contador da LP e cupom do pré-checkout).
 * O checklist manual (Wix, Beacons, checkouts, campanhas) fica salvo no banco.
 *
 * O bloco "Avançado" mantém o cupom livre (ex: BLACKFEO) e o preview técnico.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  Clock,
  ExternalLink,
  Power,
  Tag,
  Zap,
} from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatBR } from "@/lib/datetime";

import {
  getActiveCampaignFn,
  activateCampaign,
  deactivateCampaign,
  listCampaignHistory,
} from "@/lib/checkout/campanhas.functions";
import { applyCampaignCoupon } from "@/lib/checkout/campanhas";
import { CHECKOUT_LINKS, POS_INFO, ALL_PRODUCTS } from "@/lib/checkout/links";
import { ACOES, type AcaoDef } from "@/lib/campanhas/acoes";
import {
  getAcoesStateFn,
  setAcaoAtiva,
  listAcoesChecklist,
  toggleChecklistItem,
  type ChecklistRow,
} from "@/lib/campanhas/acoes.functions";

export const Route = createFileRoute("/admin/campanhas")({
  head: () => ({
    meta: [
      { title: "Admin — Campanhas de Cupom" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/campanhas">
      <CampanhasAdminPage />
    </AdminGate>
  ),
});

function CampanhasAdminPage() {
  const qc = useQueryClient();
  const getAcoes = useServerFn(getAcoesStateFn);
  const getChecklists = useServerFn(listAcoesChecklist);
  const getActive = useServerFn(getActiveCampaignFn);
  const listHistory = useServerFn(listCampaignHistory);

  const { data: acoes, isLoading } = useQuery({
    queryKey: ["admin", "acoes-state"],
    queryFn: () => getAcoes(),
  });
  const { data: checklists } = useQuery({
    queryKey: ["admin", "acoes-checklist"],
    queryFn: () => getChecklists(),
  });
  const { data: active } = useQuery({
    queryKey: ["admin", "campanha-ativa"],
    queryFn: () => getActive(),
  });
  const { data: history } = useQuery({
    queryKey: ["admin", "campanha-historico"],
    queryFn: () => listHistory(),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin", "acoes-state"] });
    qc.invalidateQueries({ queryKey: ["admin", "acoes-checklist"] });
    qc.invalidateQueries({ queryKey: ["admin", "campanha-ativa"] });
    qc.invalidateQueries({ queryKey: ["admin", "campanha-historico"] });
  };

  const plantaoAtiva = Boolean(acoes?.plantaofeo?.ativa);

  return (
    <div className="min-h-screen px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
          <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Ações de venda
          </h1>
          <p className="mt-1 text-sm text-white/60">
            Ative/desative as ações. O checklist técnico roda automaticamente; o manual fica aqui para marcar.
          </p>
          <p className="mt-2 text-xs text-white/45">
            Os slides do banner da home não são mais controlados aqui — ative/desative e reordene em{" "}
            <Link to="/admin/banner" className="font-semibold text-white/70 underline">
              /admin/banner
            </Link>
            .
          </p>
          <Link
            to="/admin/campanhas-modelo"
            className="mt-3 inline-flex rounded-lg border border-[#F5C518]/40 px-3 py-1.5 text-xs font-bold text-[#F5C518] hover:bg-[#F5C518]/10"
          >
            Modelo de ações (roteiro e checklists por tipo) →
          </Link>
        </div>

        <div className="space-y-6">
          {ACOES.map((acao) => (
            <AcaoCard
              key={acao.slug}
              acao={acao}
              ativa={Boolean(acoes?.[acao.slug]?.ativa)}
              ativadoEm={acoes?.[acao.slug]?.ativadoEm ?? null}
              loading={isLoading}
              checklist={checklists?.[acao.slug] ?? []}
              onDone={refresh}
            />
          ))}
        </div>

        {/* Avançado */}
        <AdvancedBlock
          active={active ?? null}
          plantaoAtiva={plantaoAtiva}
          onDone={refresh}
        />

        {/* Log */}
        <section className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-white/60" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-white/60">Histórico</h2>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
            {!history || history.length === 0 ? (
              <p className="p-6 text-sm text-white/60 text-center">Nenhuma ativação registrada ainda.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-white/5 text-white/60">
                  <tr>
                    <th className="p-3 text-left">Quando</th>
                    <th className="p-3 text-left">Ação</th>
                    <th className="p-3 text-left">Campanha</th>
                    <th className="p-3 text-left">Cupom</th>
                    <th className="p-3 text-left">Tipo</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {history.map((row) => (
                    <tr key={row.id} className="border-t border-white/5">
                      <td className="p-3 whitespace-nowrap">{formatBR(row.created_at)}</td>
                      <td className="p-3">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            row.action === "ativar" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/70"
                          }`}
                        >
                          {row.action}
                        </span>
                      </td>
                      <td className="p-3">{row.campaign_nome}</td>
                      <td className="p-3 font-mono text-[#F5C518]">{row.cupom}</td>
                      <td className="p-3 text-white/60">{row.is_custom ? "livre" : "preset"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function AcaoCard({
  acao,
  ativa,
  ativadoEm,
  loading,
  checklist,
  onDone,
}: {
  acao: AcaoDef;
  ativa: boolean;
  ativadoEm: string | null;
  loading: boolean;
  checklist: ChecklistRow[];
  onDone: () => void;
}) {
  const setAtiva = useServerFn(setAcaoAtiva);
  const toggleItem = useServerFn(toggleChecklistItem);
  const [busy, setBusy] = useState(false);
  const [passos, setPassos] = useState<string[] | null>(null);

  const letra = acao.slug === "plantaofeo" ? "A" : "B";
  const passosPreview = ativa ? acao.passosDesativar : acao.passosAtivar;

  const handleToggle = async () => {
    const msg = ativa
      ? `DESATIVAR "${acao.nome}"?\n\n${acao.passosDesativar.map((p) => `• ${p}`).join("\n")}`
      : `ATIVAR "${acao.nome}"?\n\n${acao.passosAtivar.map((p) => `• ${p}`).join("\n")}`;
    if (!confirm(msg)) return;
    setBusy(true);
    try {
      const res = await setAtiva({ data: { slug: acao.slug, ativa: !ativa } });
      setPassos(res.passos);
      toast.success(`${acao.nome} ${ativa ? "desativada" : "ativada"}`);
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao atualizar a ação");
    } finally {
      setBusy(false);
    }
  };

  const handleCheck = async (row: ChecklistRow, done: boolean) => {
    try {
      await toggleItem({ data: { id: row.id, done } });
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao marcar o item");
    }
  };

  const feitos = checklist.filter((c) => c.done).length;

  return (
    <section
      className={`rounded-2xl border p-6 ${
        ativa ? "border-[#F5C518]/40 bg-[#F5C518]/[0.07]" : "border-white/10 bg-white/5"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-white/40">Ação {letra}</p>
          <h2 className="mt-1 flex flex-wrap items-center gap-2 text-xl font-bold text-white">
            {acao.nome}
            {ativa ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5C518] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[#0A0F2C]">
                <Zap className="h-3 w-3" /> Ativa
              </span>
            ) : (
              <span className="rounded-full border border-white/20 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-white/50">
                Inativa
              </span>
            )}
          </h2>
          <p className="mt-1 text-sm text-white/60">{acao.escopo}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/60">
            <a
              href={acao.lp}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-white"
            >
              {acao.lp} <ExternalLink className="h-3 w-3" />
            </a>
            {acao.cupom && (
              <span className="rounded-md bg-black/40 px-2 py-1 font-mono text-xs font-bold text-[#F5C518]">
                {acao.cupom}
              </span>
            )}
            {acao.cupons?.gg?.unica && (
              <span className="rounded-md bg-black/40 px-2 py-1 font-mono text-xs font-bold text-[#4FD9FF]">
                {acao.cupons.gg.unica} (Google)
              </span>
            )}
            {ativa && ativadoEm && <span>Ativada em {formatBR(ativadoEm)}</span>}
          </div>
        </div>
        <Button
          onClick={handleToggle}
          disabled={busy || loading}
          className={
            ativa
              ? "bg-white/10 border border-white/20 text-white hover:bg-white/20 font-bold"
              : "bg-[#F5C518] hover:bg-[#e0b514] text-[#0A0F2C] font-bold"
          }
        >
          <Power className="mr-2 h-4 w-4" />
          {ativa ? `Desativar ${acao.nome}` : `Ativar ${acao.nome}`}
        </Button>
      </div>

      {/* Checklist técnico (automático) */}
      <div className="mt-5 rounded-xl border border-white/10 bg-black/25 p-4">
        <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">
          {ativa ? "Ao desativar, o sistema faz automaticamente" : "Ao ativar, o sistema faz automaticamente"}
        </p>
        <ul className="mt-2 space-y-1.5">
          {(passos ?? passosPreview).map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm text-white/75">
              <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${passos ? "text-emerald-400" : "text-white/30"}`} />
              {p}
            </li>
          ))}
        </ul>
      </div>

      {/* Checklist manual */}
      {checklist.length > 0 && (
        <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">
              Checklist manual — {checklist[0]?.phase === "inicio" ? "início da ação" : "fim da ação"}
            </p>
            <span className="text-xs font-semibold text-white/60">
              {feitos}/{checklist.length} concluídos
            </span>
          </div>
          {(["falta", "feito"] as const).map((grupo) => {
            const rows = checklist.filter((r) => (grupo === "feito" ? r.done : !r.done));
            if (rows.length === 0) return null;
            return (
              <div key={grupo} className="mt-3">
                <p className={`text-[11px] font-bold uppercase tracking-widest ${grupo === "falta" ? "text-[#F5C518]" : "text-emerald-400/80"}`}>
                  {grupo === "falta" ? `Falta fazer (${rows.length})` : `Concluído (${rows.length})`}
                </p>
                <ul className="mt-2 space-y-2.5">
                  {rows.map((row) => (
                    <li key={row.id} className="flex items-start gap-3">
                      <Checkbox
                        id={row.id}
                        checked={row.done}
                        onCheckedChange={(v) => handleCheck(row, Boolean(v))}
                        className="mt-0.5 border-white/30 data-[state=checked]:bg-[#F5C518] data-[state=checked]:text-[#0A0F2C] data-[state=checked]:border-[#F5C518]"
                      />
                      <Label
                        htmlFor={row.id}
                        className={`text-sm font-normal leading-snug ${row.done ? "text-white/40 line-through" : "text-white/85"}`}
                      >
                        {row.item_label}
                        {row.done && row.done_at && (
                          <span className="ml-2 text-[11px] text-white/35">({formatBR(row.done_at)})</span>
                        )}
                      </Label>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {checklist[0]?.phase === "inicio" && acao.checklist?.fim && (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">
            Encerramento (vira checklist ao desativar)
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-white/70">
            {acao.checklist.fim.map((it) => (
              <li key={it.key}>{it.label}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function AdvancedBlock({
  active,
  plantaoAtiva,
  onDone,
}: {
  active: { nome: string; cupom: string; isCustom: boolean; ativadoEm: string } | null;
  plantaoAtiva: boolean;
  onDone: () => void;
}) {
  const activate = useServerFn(activateCampaign);
  const deactivate = useServerFn(deactivateCampaign);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [customCupom, setCustomCupom] = useState("");
  const [customNome, setCustomNome] = useState("");

  const handleActivateCustom = async () => {
    const cupom = customCupom.trim().toUpperCase();
    if (!cupom) return toast.error("Digite um cupom");
    setBusy(true);
    try {
      const res = await activate({ data: { kind: "custom", cupom, nome: customNome.trim() || undefined } });
      toast.success(`Campanha "${res.campaign.nome}" ativada`);
      setCustomCupom("");
      setCustomNome("");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao ativar");
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async () => {
    if (!confirm("Voltar ao fluxo PADRÃO (lote promocional)?")) return;
    setBusy(true);
    try {
      await deactivate();
      toast.success("Fluxo padrão restaurado");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao desativar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left"
      >
        <span className="text-sm font-bold uppercase tracking-widest text-white/60">
          Avançado — cupom livre e preview técnico
        </span>
        <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-white/10 p-6">
          {/* Estado do cupom global */}
          {active ? (
            <div className="mb-6 rounded-xl border border-[#F5C518]/40 bg-[#F5C518]/10 p-5">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-white font-bold">{active.nome}</span>
                <span className="rounded-md bg-black/40 px-2 py-1 font-mono text-xs font-bold text-[#F5C518]">
                  {active.cupom}
                </span>
                <span className="rounded-full border border-white/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/60">
                  {active.isCustom ? "cupom livre" : "ação"}
                </span>
              </div>
              <p className="mt-3 text-xs text-white/60">Aplicado desde {formatBR(active.ativadoEm)}</p>
              {active.isCustom && (
                <Button
                  onClick={handleDeactivate}
                  disabled={busy}
                  variant="outline"
                  className="mt-4 bg-white/5 border-white/20 text-white hover:bg-white/10"
                >
                  Voltar ao fluxo padrão (lote promocional)
                </Button>
              )}
            </div>
          ) : (
            <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.02] p-5 text-center">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-400" />
              <p className="font-semibold text-white">Fluxo PADRÃO ativo</p>
              <p className="mt-1 text-xs text-white/60">
                Cupons <span className="font-mono">LOTEPROMO</span> /{" "}
                <span className="font-mono">LOTEPROMOASS</span>
              </p>
            </div>
          )}

          {/* Cupom livre */}
          <div className="mb-8">
            <div className="mb-2 flex items-center gap-2">
              <Tag className="h-5 w-5 text-[#F5C518]" />
              <h3 className="font-bold text-white">Ativar cupom livre</h3>
            </div>
            <p className="mb-4 text-xs text-white/60">
              Para ações sem LP própria (ex: <span className="font-mono">BLACKFEO</span>). Troca o cupom das 6 pós
              sem mexer nos slides da home.
            </p>
            {plantaoAtiva && (
              <p className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200">
                O Plantão FEO está ativo e já controla o cupom das pós. Desative-o antes de usar um cupom livre.
              </p>
            )}
            <div className="grid items-end gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <div>
                <Label htmlFor="cupom" className="text-xs text-white/80">Cupom (obrigatório)</Label>
                <Input
                  id="cupom"
                  value={customCupom}
                  onChange={(e) => setCustomCupom(e.target.value.toUpperCase())}
                  placeholder="CUPOMEXEMPLO"
                  className="mt-1 bg-white/5 border-white/10 font-mono uppercase text-white"
                  maxLength={40}
                />
              </div>
              <div>
                <Label htmlFor="nome" className="text-xs text-white/80">Nome (opcional)</Label>
                <Input
                  id="nome"
                  value={customNome}
                  onChange={(e) => setCustomNome(e.target.value)}
                  placeholder="Ação Black Friday"
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  maxLength={80}
                />
              </div>
              <Button
                onClick={handleActivateCustom}
                disabled={busy || plantaoAtiva || !customCupom.trim()}
                className="bg-[#F5C518] font-bold text-[#0A0F2C] hover:bg-[#e0b514]"
              >
                Ativar
              </Button>
            </div>
          </div>

          {/* Preview técnico */}
          <h3 className="mb-3 text-sm font-bold uppercase tracking-widest text-white/60">
            Links atuais do pré-checkout (fluxo padrão)
          </h3>
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/30">
            <table className="w-full text-xs">
              <thead className="bg-white/5 text-white/70">
                <tr>
                  <th className="p-3 text-left">Pós</th>
                  <th className="p-3 text-left">Única</th>
                  <th className="p-3 text-left">Assinatura</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                {ALL_PRODUCTS.map((slug) => {
                  const base = CHECKOUT_LINKS[slug];
                  const unica = active?.cupom ? applyCampaignCoupon(base.unica, active.cupom) : base.unica;
                  const assinatura = active?.cupom
                    ? applyCampaignCoupon(base.assinatura, active.cupom)
                    : base.assinatura;
                  return (
                    <tr key={slug} className="border-t border-white/5">
                      <td className="p-3 font-semibold text-white whitespace-nowrap">{POS_INFO[slug].nomeCurto}</td>
                      <td className="p-3 font-mono break-all">
                        <a href={unica} target="_blank" rel="noreferrer" className="hover:text-[#F5C518]">
                          {unica}
                        </a>
                      </td>
                      <td className="p-3 font-mono break-all">
                        <a href={assinatura} target="_blank" rel="noreferrer" className="hover:text-[#F5C518]">
                          {assinatura}
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
